import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Row,
    Select, Space, Spin, Table, Tag, Typography, Upload, message
} from 'antd';
import {
    DeleteOutlined, EditOutlined, FilePdfOutlined, PlusOutlined,
    ReloadOutlined, SettingOutlined, StopOutlined
} from '@ant-design/icons';
import {
    getGlobalDocumentTypesAPI,
    getOrganizationClinicalServicesAPI,
    getOrganizationManagementProductsAPI,
    getOrganizationProcedureDocumentsAPI,
    getProcedureConfigurationsAPI,
    getProcedureDocumentAssignmentsAPI,
    postOrganizationProcedureDocumentAPI,
    postProcedureConfigurationAPI,
    postProcedureDocumentAssignmentAPI,
    putOrganizationProcedureDocumentAPI,
    putProcedureConfigurationAPI,
    deleteProcedureDocumentAssignmentAPI,
    inactivateOrganizationProcedureDocumentAPI,
    getOrganizationProcedureDocumentFileAPI
} from '../store/user/restCallUser';

type Row = Record<string, any>;

interface Props {
    organizationId: string;
}

const valueOf = (row: Row | undefined, ...names: string[]) => {
    for (const name of names) {
        if (row?.[name] !== undefined && row?.[name] !== null) return row[name];
    }
    return undefined;
};

const idOf = (row: Row | undefined) => String(valueOf(row, 'id', 'Id') || '');

const unwrap = (response: any): any => {
    let value = response?.Response ?? response?.data ?? response;
    for (let i = 0; i < 4; i += 1) {
        if (typeof value === 'string') {
            try { value = JSON.parse(value); } catch { return value; }
            continue;
        }
        if (value && !Array.isArray(value) && typeof value === 'object') {
            if (value.data !== undefined) { value = value.data; continue; }
            if (value.Response !== undefined) { value = value.Response; continue; }
        }
        break;
    }
    return value;
};

const listOf = (response: any): Row[] => {
    const value = unwrap(response);
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.items)) return value.items;
    if (value && Array.isArray(value.Items)) return value.Items;
    return [];
};

const apiError = (response: any): string | null => {
    if (!response) return 'No se recibió respuesta del servidor.';
    if (response.Error !== null && response.Error !== undefined) {
        return String(response.Error?.message || response.Error);
    }
    const status = Number(response.StatusCode ?? response.status ?? 200);
    return status >= 200 && status < 300 ? null : String(response.message || 'No se pudo completar la operación.');
};

const normalizedCategory = (row: Row) => String(valueOf(row, 'category', 'Category') || '').trim().toUpperCase();

const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = String(reader.result || '');
        resolve(result.includes(',') ? result.substring(result.indexOf(',') + 1) : result);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
});

export default function OrganizationProcedureManagement({ organizationId }: Props) {
    const [services, setServices] = useState<Row[]>([]);
    const [products, setProducts] = useState<Row[]>([]);
    const [procedures, setProcedures] = useState<Row[]>([]);
    const [documents, setDocuments] = useState<Row[]>([]);
    const [documentTypes, setDocumentTypes] = useState<Row[]>([]);
    const [assignments, setAssignments] = useState<Row[]>([]);
    const [selectedProcedure, setSelectedProcedure] = useState<Row | null>(null);
    const [procedureModalOpen, setProcedureModalOpen] = useState(false);
    const [documentModalOpen, setDocumentModalOpen] = useState(false);
    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const [editingProcedure, setEditingProcedure] = useState<Row | null>(null);
    const [editingDocument, setEditingDocument] = useState<Row | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [procedureForm] = Form.useForm();
    const [documentForm] = Form.useForm();
    const [assignmentForm] = Form.useForm();

    const procedureServices = useMemo(
        () => services.filter(item => normalizedCategory(item) === 'PROCEDIMIENTOS'),
        [services]
    );
    const procedureServiceIds = useMemo(
        // clinical-services devuelve dos identificadores: `id` es la
        // asociación de la organización y `serviceId` es el id de str.service.
        // Los productos de pdt.products guardan el segundo.
        () => new Set(procedureServices.map(item => String(valueOf(item, 'serviceId', 'ServiceId') || ''))),
        [procedureServices]
    );
    const procedureProducts = useMemo(() => products.filter(item => {
        const organizationProductId = valueOf(item, 'organizationProductId', 'OrganizationProductId');
        const serviceId = valueOf(item, 'serviceId', 'ServiceId');
        return organizationProductId && procedureServiceIds.has(String(serviceId));
    }), [products, procedureServiceIds]);
    const configuredByProduct = useMemo(
        () => new Map(procedures.map(item => [String(valueOf(item, 'organizationProductId', 'OrganizationProductId')), item])),
        [procedures]
    );
    const procedureRows = useMemo(() => procedureProducts.map(product => {
        const organizationProductId = String(valueOf(product, 'organizationProductId', 'OrganizationProductId'));
        const configured = configuredByProduct.get(organizationProductId);
        const service = procedureServices.find(item => String(valueOf(item, 'serviceId', 'ServiceId') || '') === String(valueOf(product, 'serviceId', 'ServiceId') || ''));
        return {
            ...product,
            procedure: configured,
            procedureId: configured ? idOf(configured) : '',
            serviceName: valueOf(service, 'name', 'Name') || 'Procedimientos'
        };
    }), [procedureProducts, configuredByProduct, procedureServices]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const responses = await Promise.all([
                getOrganizationClinicalServicesAPI(organizationId),
                getOrganizationManagementProductsAPI(organizationId, undefined, false),
                getProcedureConfigurationsAPI(organizationId),
                getOrganizationProcedureDocumentsAPI(organizationId, 'ACTIVO'),
                getGlobalDocumentTypesAPI()
            ]);
            const failure = responses.map(apiError).find(Boolean);
            if (failure) throw new Error(String(failure));
            setServices(listOf(responses[0]));
            setProducts(listOf(responses[1]));
            setProcedures(listOf(responses[2]));
            setDocuments(listOf(responses[3]));
            setDocumentTypes(listOf(responses[4]));
        } catch (e: any) {
            setError(e?.message || 'No se pudo cargar la configuración de procedimientos.');
        } finally { setLoading(false); }
    }, [organizationId]);

    useEffect(() => { void load(); }, [load]);

    const saveProcedure = async (values: Row) => {
        try {
            setSaving(true);
            const payload = {
                organizationProductId: values.organizationProductId,
                type: values.type,
                hasConsent: Boolean(values.hasConsent),
                hasAppointment: values.hasAppointment,
                appointmentMinutesTime: Number(values.appointmentMinutesTime || 0),
                hasAnesthesiologist: Boolean(values.hasAnesthesiologist),
                durationMinutes: Number(values.durationMinutes || 0)
            };
            const response = editingProcedure
                ? await putProcedureConfigurationAPI(organizationId, idOf(editingProcedure), payload)
                : await postProcedureConfigurationAPI(organizationId, payload);
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setProcedures(listOf(response));
            setProcedureModalOpen(false);
            procedureForm.resetFields();
            message.success(editingProcedure ? 'Procedimiento actualizado.' : 'Procedimiento configurado.');
        } catch (e: any) { message.error(e?.message || 'No se pudo guardar el procedimiento.'); }
        finally { setSaving(false); }
    };

    const openProcedure = (row: Row) => {
        const procedure = row.procedure as Row | undefined;
        setEditingProcedure(procedure || null);
        procedureForm.setFieldsValue(procedure || {
            organizationProductId: valueOf(row, 'organizationProductId', 'OrganizationProductId'),
            type: 'TERAPEUTICO', hasConsent: false, hasAppointment: 'NO',
            appointmentMinutesTime: 0, hasAnesthesiologist: false, durationMinutes: 0
        });
        setProcedureModalOpen(true);
    };

    const openAssignments = async (row: Row) => {
        const procedure = row.procedure as Row | undefined;
        if (!procedure) { message.warning('Configure primero el procedimiento.'); return; }
        try {
            const response = await getProcedureDocumentAssignmentsAPI(organizationId, idOf(procedure));
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setAssignments(listOf(response));
            setSelectedProcedure({ ...row, procedure });
            assignmentForm.resetFields();
            setAssignmentModalOpen(true);
        } catch (e: any) { message.error(e?.message || 'No se pudieron cargar los documentos asignados.'); }
    };

    const addAssignment = async (values: Row) => {
        if (!selectedProcedure?.procedure) return;
        try {
            const response = await postProcedureDocumentAssignmentAPI(
                organizationId,
                idOf(selectedProcedure.procedure),
                values
            );
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setAssignments(listOf(response));
            assignmentForm.resetFields();
            message.success('Documento asignado al procedimiento.');
        } catch (e: any) { message.error(e?.message || 'No se pudo asignar el documento.'); }
    };

    const removeAssignment = async (row: Row) => {
        if (!selectedProcedure?.procedure) return;
        try {
            const response = await deleteProcedureDocumentAssignmentAPI(
                organizationId,
                idOf(selectedProcedure.procedure),
                idOf(row)
            );
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setAssignments(listOf(response));
            message.success('Asignación eliminada.');
        } catch (e: any) { message.error(e?.message || 'No se pudo eliminar la asignación.'); }
    };

    const saveDocument = async (values: Row) => {
        try {
            setSaving(true);
            const payload: Row = {
                name: values.name,
                description: values.description || null,
                typeDocumentsId: values.typeDocumentsId,
                format: values.format,
                status: values.status || 'ACTIVO',
                daysValidity: Number(values.daysValidity || 0),
                template: values.format === 'HTML' ? (values.template || null) : null
            };
            const selectedPdf = Array.isArray(values.pdfFile) ? values.pdfFile[0]?.originFileObj : values.pdfFile?.originFileObj;
            if (selectedPdf) {
                const file = selectedPdf as File;
                payload.pdfBase64 = await readFileAsBase64(file);
                payload.pdfFileName = file.name;
                payload.pdfContentType = file.type || 'application/pdf';
            }
            const response = editingDocument
                ? await putOrganizationProcedureDocumentAPI(organizationId, idOf(editingDocument), payload)
                : await postOrganizationProcedureDocumentAPI(organizationId, payload);
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setDocuments(listOf(response));
            setDocumentModalOpen(false);
            documentForm.resetFields();
            message.success(editingDocument ? 'Documento actualizado.' : 'Documento creado.');
        } catch (e: any) { message.error(e?.message || 'No se pudo guardar el documento.'); }
        finally { setSaving(false); }
    };

    const openDocument = (row?: Row) => {
        setEditingDocument(row || null);
        documentForm.setFieldsValue(row ? {
            ...row,
            typeDocumentsId: valueOf(row, 'typeDocumentsId', 'TypeDocumentsId'),
            format: String(valueOf(row, 'format', 'Format') || 'HTML').toUpperCase()
        } : {
            format: 'HTML', status: 'ACTIVO', daysValidity: 365
        });
        setDocumentModalOpen(true);
    };

    const inactivateDocument = async (row: Row) => {
        try {
            const response = await inactivateOrganizationProcedureDocumentAPI(organizationId, idOf(row));
            const failure = apiError(response);
            if (failure) throw new Error(failure);
            setDocuments(listOf(response));
            message.success('Documento inactivado.');
        } catch (e: any) { message.error(e?.message || 'No se pudo inactivar el documento.'); }
    };

    const viewPdf = async (row: Row) => {
        try {
            const response = await getOrganizationProcedureDocumentFileAPI(organizationId, idOf(row));
            const data = unwrap(response);
            const url = valueOf(data, 'url', 'Url', 'downloadUrl', 'DownloadUrl');
            if (!url) throw new Error('El servidor no devolvió un enlace temporal.');
            window.open(String(url), '_blank', 'noopener,noreferrer');
        } catch (e: any) { message.error(e?.message || 'No se pudo abrir el PDF.'); }
    };

    return <Card
        title="Servicios y precios · Procedimientos"
        extra={<Button icon={<ReloadOutlined />} onClick={() => void load()}>Actualizar</Button>}
    >
        {error && <Alert type="error" showIcon closable message={error} style={{ marginBottom: 16 }} />}
        <Alert
            type="info"
            showIcon
            message="Esta sección solo aplica al servicio PROCEDIMIENTOS"
            description="Los productos se toman de la organización. Aquí se configura pdt.procedure y se asignan los documentos operativos (HTML o PDF). Laboratorio, Imagen y los demás servicios no aparecen en esta pantalla."
            style={{ marginBottom: 16 }}
        />
        <Spin spinning={loading}>
            <Card
                size="small"
                title="Procedimientos configurables"
                extra={<Tag color="blue">{procedureServices.length} servicio(s) PROCEDIMIENTOS</Tag>}
            >
                {!procedureServices.length
                    ? <Empty description="No existe un servicio con categoría PROCEDIMIENTOS" />
                    : <Table
                        rowKey={(row) => String(valueOf(row, 'organizationProductId', 'OrganizationProductId'))}
                        dataSource={procedureRows}
                        pagination={{ pageSize: 8 }}
                        locale={{ emptyText: <Empty description="No hay productos asociados a PROCEDIMIENTOS" /> }}
                        columns={[
                            { title: 'Producto', render: (_: any, row: Row) => valueOf(row, 'commercialName', 'CommercialName', 'description', 'Description') || 'Sin nombre' },
                            { title: 'Código', dataIndex: 'organizationCode' },
                            { title: 'Servicio', dataIndex: 'serviceName' },
                            { title: 'Configuración', render: (_: any, row: Row) => row.procedure ? <Tag color="green">CONFIGURADO</Tag> : <Tag color="orange">PENDIENTE</Tag> },
                            { title: 'Documentos', render: (_: any, row: Row) => <Button size="small" icon={<FilePdfOutlined />} disabled={!row.procedure} onClick={() => void openAssignments(row)}>Gestionar documentos</Button> },
                            { title: 'Acciones', render: (_: any, row: Row) => <Button type="primary" size="small" icon={<SettingOutlined />} onClick={() => openProcedure(row)}>{row.procedure ? 'Editar' : 'Configurar'}</Button> }
                        ]}
                    />}
            </Card>

            <Card
                size="small"
                title="Documentos operativos de la organización"
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openDocument()}>Nuevo documento</Button>}
                style={{ marginTop: 16 }}
            >
                <Table
                    rowKey={idOf}
                    dataSource={documents}
                    pagination={{ pageSize: 8 }}
                    locale={{ emptyText: <Empty description="No hay documentos operativos activos" /> }}
                    columns={[
                        { title: 'Nombre', dataIndex: 'name' },
                        { title: 'Tipo', render: (_: any, row: Row) => valueOf(row, 'typeDocumentName', 'TypeDocumentName') || valueOf(row, 'typeDocumentCode', 'TypeDocumentCode') },
                        { title: 'Formato', render: (_: any, row: Row) => <Tag>{String(valueOf(row, 'format', 'Format') || '').toUpperCase()}</Tag> },
                        { title: 'Vigencia', render: (_: any, row: Row) => `${valueOf(row, 'daysValidity', 'DaysValidity') || 0} días` },
                        { title: 'Acciones', render: (_: any, row: Row) => <Space wrap><Button size="small" icon={<EditOutlined />} onClick={() => openDocument(row)}>Editar</Button>{String(valueOf(row, 'format', 'Format')).toUpperCase() === 'PDF' && <Button size="small" icon={<FilePdfOutlined />} onClick={() => void viewPdf(row)}>Ver PDF</Button>}<Button size="small" danger icon={<StopOutlined />} onClick={() => void inactivateDocument(row)}>Inactivar</Button></Space> }
                    ]}
                />
            </Card>
        </Spin>

        <Modal
            title={editingProcedure ? 'Editar procedimiento' : 'Configurar procedimiento'}
            open={procedureModalOpen}
            onCancel={() => setProcedureModalOpen(false)}
            onOk={() => procedureForm.submit()}
            confirmLoading={saving}
            okText="Guardar"
            cancelText="Cancelar"
        >
            <Form form={procedureForm} layout="vertical" onFinish={saveProcedure}>
                <Form.Item name="organizationProductId" label="Producto de la organización" rules={[{ required: true, message: 'Seleccione un producto.' }]}>
                    <Select disabled={Boolean(editingProcedure)} options={procedureProducts.map(row => ({ value: valueOf(row, 'organizationProductId', 'OrganizationProductId'), label: `${valueOf(row, 'commercialName', 'CommercialName', 'description', 'Description')} · ${valueOf(row, 'organizationCode', 'OrganizationCode', 'code', 'Code') || ''}` }))} />
                </Form.Item>
                <Row gutter={12}><Col span={12}><Form.Item name="type" label="Tipo" rules={[{ required: true }]}><Select options={[{ value: 'DIAGNOSTICO', label: 'Diagnóstico' }, { value: 'TERAPEUTICO', label: 'Terapéutico' }]} /></Form.Item></Col><Col span={12}><Form.Item name="hasAppointment" label="¿Requiere cita?" rules={[{ required: true }]}><Select options={[{ value: 'SI', label: 'Sí' }, { value: 'NO', label: 'No' }, { value: 'OPCIONAL', label: 'Opcional' }]} /></Form.Item></Col></Row>
                <Row gutter={12}><Col span={12}><Form.Item name="appointmentMinutesTime" label="Duración de cita (minutos)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col><Col span={12}><Form.Item name="durationMinutes" label="Duración del procedimiento (minutos)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col></Row>
                <Form.Item name="hasConsent" label="Consentimiento"><Select options={[{ value: true, label: 'Requiere consentimiento' }, { value: false, label: 'No requiere consentimiento' }]} /></Form.Item>
                <Form.Item name="hasAnesthesiologist" label="Anestesiólogo"><Select options={[{ value: true, label: 'Requiere anestesiólogo' }, { value: false, label: 'No requiere anestesiólogo' }]} /></Form.Item>
            </Form>
        </Modal>

        <Modal
            title={`Documentos del procedimiento${selectedProcedure ? ` · ${valueOf(selectedProcedure, 'commercialName', 'CommercialName', 'description', 'Description')}` : ''}`}
            open={assignmentModalOpen}
            onCancel={() => setAssignmentModalOpen(false)}
            footer={<Button onClick={() => setAssignmentModalOpen(false)}>Cerrar</Button>}
            width={760}
        >
            <Form form={assignmentForm} layout="inline" onFinish={addAssignment} style={{ marginBottom: 16 }}>
                <Form.Item name="documentsId" rules={[{ required: true, message: 'Seleccione un documento.' }]}><Select placeholder="Documento activo" style={{ width: 280 }} options={documents.map(row => ({ value: idOf(row), label: `${valueOf(row, 'name', 'Name')} (${valueOf(row, 'format', 'Format')})` }))} /></Form.Item>
                <Form.Item name="usage" rules={[{ required: true, message: 'Seleccione el uso.' }]}><Select placeholder="Uso" style={{ width: 190 }} options={[{ value: 'MOSTRAR', label: 'Mostrar' }, { value: 'FIRMAR', label: 'Firmar' }, { value: 'PREPARAR', label: 'Preparar' }, { value: 'COMPLETAR_RESULTADO', label: 'Completar resultado' }]} /></Form.Item>
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Asignar</Button>
            </Form>
            <Table rowKey={idOf} dataSource={assignments} pagination={false} columns={[{ title: 'Documento', dataIndex: 'documentName' }, { title: 'Formato', dataIndex: 'documentFormat' }, { title: 'Uso', dataIndex: 'usage' }, { title: 'Acciones', render: (_: any, row: Row) => <Button danger size="small" icon={<DeleteOutlined />} onClick={() => void removeAssignment(row)}>Quitar</Button> }]} locale={{ emptyText: <Empty description="No hay documentos asignados" /> }} />
        </Modal>

        <Modal
            title={editingDocument ? 'Editar documento operativo' : 'Nuevo documento operativo'}
            open={documentModalOpen}
            onCancel={() => setDocumentModalOpen(false)}
            onOk={() => documentForm.submit()}
            confirmLoading={saving}
            okText="Guardar"
            cancelText="Cancelar"
            width={820}
        >
            <Alert type="info" showIcon message="Documento de la organización" description="El tipo proviene del catálogo global. Para HTML se usa una plantilla reutilizable; para PDF se carga el archivo en Azure Blob Storage mediante el backend." style={{ marginBottom: 16 }} />
            <Form form={documentForm} layout="vertical" onFinish={saveDocument}>
                <Row gutter={12}><Col span={14}><Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre.' }]}><Input maxLength={255} /></Form.Item></Col><Col span={10}><Form.Item name="typeDocumentsId" label="Tipo global" rules={[{ required: true, message: 'Seleccione el tipo.' }]}><Select options={documentTypes.map(row => ({ value: idOf(row), label: `${valueOf(row, 'name', 'Name')} (${valueOf(row, 'code', 'Code')})` }))} /></Form.Item></Col></Row>
                <Form.Item name="description" label="Descripción"><Input.TextArea rows={2} /></Form.Item>
                <Row gutter={12}><Col span={8}><Form.Item name="format" label="Formato" rules={[{ required: true }]}><Select options={[{ value: 'HTML', label: 'HTML / plantilla' }, { value: 'PDF', label: 'PDF' }]} /></Form.Item></Col><Col span={8}><Form.Item name="daysValidity" label="Días de vigencia desde la emisión" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col><Col span={8}><Form.Item name="status" label="Estado"><Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} /></Form.Item></Col></Row>
                <Form.Item noStyle shouldUpdate={(prev, next) => prev.format !== next.format}>{({ getFieldValue }) => getFieldValue('format') === 'PDF' ? <Form.Item name="pdfFile" label="Archivo PDF" valuePropName="fileList" getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}><Upload beforeUpload={() => false} maxCount={1} accept="application/pdf"><Button icon={<FilePdfOutlined />}>Seleccionar PDF</Button></Upload></Form.Item> : <Form.Item name="template" label="Plantilla HTML"><Input.TextArea rows={12} placeholder={'HTML de la plantilla. Use {{TOKEN}} y data-field="CAMPO".'} /></Form.Item>}</Form.Item>
            </Form>
        </Modal>
    </Card>;
}
